import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { Check, X, Info, AlertTriangle } from "lucide-react";

const FADE_DURATION = 300;

const NormalAlert = ({ type = "info", message, onClose, autoClose = 3000 }) => {
  const normalizedType =
    type.toLowerCase() === "fail" ? "error" : type.toLowerCase();

  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => setVisible(true), []);

  useEffect(() => {
    if (!onClose || !autoClose) return;

    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(autoClose - elapsed, 0);
      setProgress((remaining / autoClose) * 100);
      if (remaining <= 0) clearInterval(interval);
    }, 50);

    const closeTimer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, FADE_DURATION);
    }, autoClose);

    return () => {
      clearTimeout(closeTimer);
      clearInterval(interval);
    };
  }, [onClose, autoClose]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, FADE_DURATION);
  };

  const types = {
    success: {
      color: "text-green-600",
      bg: "bg-green-100",
      bar: "bg-green-500",
      icon: <Check size={18} />,
    },
    error: {
      color: "text-red-600",
      bg: "bg-red-100",
      bar: "bg-red-500",
      icon: <X size={18} />,
    },
    info: {
      color: "text-sky-600",
      bg: "bg-sky-100",
      bar: "bg-sky-500",
      icon: <Info size={18} />,
    },
    warning: {
      color: "text-amber-600",
      bg: "bg-amber-100",
      bar: "bg-amber-500",
      icon: <AlertTriangle size={18} />,
    },
  };

  const style = types[normalizedType] || types.info;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/35 flex justify-center sm:justify-end items-end px-3 sm:px-0 z-[9999]"
      onClick={handleClose}
    >
      <div
        className={`relative top-[-15px] sm:top-[-25px]  right-0 sm:right-[25px] w-full sm:w-auto max-w-md sm:min-w-[300px] rounded-xl shadow-xl bg-gradient-to-b from-white to-gray-50 overflow-hidden transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
      >
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gray-200">
          <div
            className={`h-full ${style.bar} transition-all duration-100`}
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Content */}
        <div className="flex items-center gap-4 px-5 py-4">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${style.bg} ${style.color}`}
          >
            {style.icon}
          </div>

          <div className="flex-1 text-[15px] font-medium text-gray-800 leading-snug">
            {message}
          </div>

          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-black text-lg transition-transform duration-200 hover:scale-110"
          >
            <X size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default NormalAlert;
